import React, { useRef, useState, useEffect } from "react";
import { WebVTT } from "videojs-vtt.js";
import cx from "classnames";

export const TranscriptSidebar = props => {
    const { textTracks, defaultTrack, playheadTime, onSeek } = props;

    const firstCueRef = useRef();
    const activeCueRef = useRef(null);
    const trackContainerRef = useRef();

    const [ selectedTrack, setSelectedTrack ] = useState(defaultTrack);
    const [ cues, setCues ] = useState([]);
    const [ isScrolled, setScrolled ] = useState(false);
    const [ isClosed, setClosed ] = useState(false);

    // Fetch and parse the VTT file whenever the selected language changes
    useEffect(() => {
        const track = textTracks.find(t => t.language === selectedTrack);
        if (!track) return;

        const vttFetch = async () => {
            const response = await fetch(track.storage);
            const text = await response.text();

            let parsedCues = [];
            const parser = new WebVTT.Parser(window, WebVTT.StringDecoder());
            parser.oncue = cue => parsedCues.push(cue);
            parser.parse(text);
            parser.flush();

            setCues(parsedCues);
        };
        vttFetch();
    }, [ textTracks, selectedTrack ]);

    // Attach IntersectionObserver to the first cue to show a shadow on the header
    // when the user has scrolled down past it
    useEffect(() => {
        if (!firstCueRef?.current || !trackContainerRef?.current) { return; }

        const callback = entries => {
            setScrolled(!entries[0].isIntersecting);
        };

        const observer = new IntersectionObserver(callback, {
            root: trackContainerRef.current,
            threshold: 0.75
        });
        observer.observe(firstCueRef.current);

        return () => observer.unobserve(firstCueRef.current);
    }, [ cues ]);

    // Auto-scroll the active cue into view whenever the playhead moves.
    // activeCueRef.current is updated during the render that precedes this effect.
    useEffect(() => {
        const el = activeCueRef.current;
        const container = trackContainerRef.current;
        if (!el || !container) return;

        const elTop = el.offsetTop;
        const elBottom = elTop + el.offsetHeight;
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;

        // Only scroll when the cue is meaningfully out of view (15 px buffer)
        if (elTop < containerTop + 15 || elBottom > containerBottom - 15) {
            container.scrollTo({ top: elTop - 15, behavior: 'smooth' });
        }
    }, [ playheadTime ]);

    const handleCueActivation = startTime => {
        onSeek(startTime + 0.05);
    };

    if (isClosed) { return null; }

    return (
        <div
            className="player-sidebar"
            role="region"
            aria-label={ Omeka.jsTranslate("Transcript") }
        >
            <div className={ cx("player-header", { "scrolled": isScrolled }) }>
                <select
                    value={ selectedTrack }
                    onChange={ event => setSelectedTrack(event.target.value) }
                    aria-label={ Omeka.jsTranslate("Transcript language") }
                >
                    { textTracks.map(track => (
                        <option
                            value={ track.language }
                            key={ track.language }
                        >
                            { track["language-label"] }
                        </option>
                    )) }
                </select>
                <button
                    className="player-close fas fa-times"
                    aria-label={ Omeka.jsTranslate("Close transcript") }
                    onClick={ () => setClosed(true) }
                />
            </div>
            <div
                ref={ trackContainerRef }
                className="player-track-container"
            >
                <div
                    className="player-track"
                    lang={ selectedTrack }
                >
                    { cues.map((cue, index) => {
                        const isActive = cue.startTime <= playheadTime &&
                            cue.endTime >= playheadTime;

                        // firstCueRef: always on index 0 (used by IntersectionObserver)
                        // activeCueRef: on the first active cue (used by auto-scroll effect)
                        let ref = null;
                        if (index === 0) {
                            ref = firstCueRef;
                        } else if (isActive) {
                            ref = activeCueRef;
                        }

                        return (
                            <p
                                ref={ ref }
                                className={ isActive ? "active" : "" }
                                role="button"
                                tabIndex={ 0 }
                                onClick={ () => handleCueActivation(cue.startTime) }
                                onKeyDown={ e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleCueActivation(cue.startTime);
                                    }
                                } }
                                key={ `${cue.startTime}-${cue.endTime}` }
                            >
                                { cue.text }
                            </p>
                        );
                    }) }
                </div>
            </div>
        </div>
    );
};
